import React, { useEffect, useRef, useState } from 'react';
import { DESCRIPTION_MAX_LENGTH } from '../../utils/profile.utils';
import './ProfileDescription.scss';

const DEFAULT_PLACEHOLDER =
  'Add a description E.g. “Passionate traveler seeking authentic experiences through local artisan workshops, traditional crafts, and cultural immersion in every destination.”';

type ProfileDescriptionProps = {
  value: string;
  onSave: (description: string) => void;
  placeholder?: string;
  isEditing: boolean;
  onEditingChange: (isEditing: boolean) => void;
};

/** Bio text that becomes a textarea on double click (or Enter when focused). */
function ProfileDescription({
  value,
  onSave,
  placeholder = DEFAULT_PLACEHOLDER,
  isEditing,
  onEditingChange: setIsEditing,
}: ProfileDescriptionProps) {
  const [draft, setDraft] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Seed the draft only when editing starts, whichever control triggered it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `value` must not reseed mid-edit
  useEffect(() => {
    if (isEditing) {
      setDraft(value);
      const textarea = textareaRef.current;
      textarea?.focus();
      textarea?.setSelectionRange(value.length, value.length);
    }
  }, [isEditing]);

  const startEditing = () => setIsEditing(true);

  const commit = () => {
    setIsEditing(false);
    const next = draft.trim();
    if (next !== value) {
      onSave(next);
    }
  };

  if (!isEditing) {
    return (
      <div
        className={`profile-description${value ? '' : ' empty'}`}
        role='button'
        tabIndex={0}
        title='Double click to edit'
        onDoubleClick={startEditing}
        onKeyDown={(event) => event.key === 'Enter' && startEditing()}
      >
        {value || placeholder}
      </div>
    );
  }

  return (
    <div className='profile-description-editor'>
      <textarea
        ref={textareaRef}
        className='profile-description-input'
        aria-label='Profile description'
        value={draft}
        maxLength={DESCRIPTION_MAX_LENGTH}
        rows={3}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setDraft(value);
            setIsEditing(false);
          } else if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            commit();
          }
        }}
      />
      <span className='profile-description-counter'>
        {draft.length}/{DESCRIPTION_MAX_LENGTH}
      </span>
    </div>
  );
}

ProfileDescription.displayName = 'ProfileDescription';
export default ProfileDescription;
